import {ClockIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Heading, NavList, Stack, Text, asSlot} from '@primer/react'
import {useEffect, useId, useRef, useState} from 'react'
import styles from './StackList.module.css'

const disableTooltipProps = {unsafeDisableTooltip: true}

export const AutoMergeRangeAction = asSlot(function AutoMergeRangeActionSlot({
  pullRequestCount,
}: {
  pullRequestCount: number
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  // Left empty so the overlay does not move focus back to the anchor when it closes; `onClose` returns focus on Escape.
  const noReturnFocusRef = useRef<HTMLElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  // Remove 1 from the pullRequestCount to account for the PR actively being viewed.
  const otherPullRequestCount = Math.max(pullRequestCount - 1, 0)
  const description =
    otherPullRequestCount === 0
      ? 'This pull request will automatically merge when it meets the merge requirements.'
      : `This pull request and ${otherPullRequestCount} ${
          otherPullRequestCount === 1 ? 'other' : 'others'
        } in the stack will automatically merge when they all meet the merge requirements.`

  // AnchoredOverlay only closes on Escape or an outside click, so tabbing to another indicator would leave this one
  // open alongside the next. Close it as soon as focus moves outside the anchor and overlay, leaving focus where the
  // user moved it.
  useEffect(() => {
    if (!open) return

    const closeWhenFocusLeaves = (event: FocusEvent) => {
      const target = event.target as Node | null
      if (anchorRef.current?.contains(target) || overlayRef.current?.contains(target)) return
      setOpen(false)
    }

    document.addEventListener('focusin', closeWhenFocusLeaves)
    return () => document.removeEventListener('focusin', closeWhenFocusLeaves)
  }, [open])

  return (
    <AnchoredOverlay
      anchorRef={anchorRef}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={gesture => {
        setOpen(false)
        if (gesture === 'escape' || gesture === 'close') anchorRef.current?.focus()
      }}
      side="outside-left"
      align="start"
      width="medium"
      overlayProps={{
        ref: overlayRef,
        returnFocusRef: noReturnFocusRef,
        role: 'dialog',
        'aria-labelledby': titleId,
        'aria-describedby': descriptionId,
      }}
      renderAnchor={props => (
        <NavList.TrailingAction
          {...props}
          aria-haspopup="dialog"
          {...disableTooltipProps}
          as="button"
          className={styles.autoMergeRangeAction}
          icon={ClockIcon}
          label="Auto-merge enabled"
          tooltipDirection="w"
        />
      )}
    >
      <Stack padding="normal" gap="condensed">
        <Heading as="h3" id={titleId} className="f5">
          Auto-merge enabled
        </Heading>
        <Text id={descriptionId}>{description}</Text>
      </Stack>
    </AnchoredOverlay>
  )
}, NavList.TrailingAction)
